import React, { useState } from 'react';
import axios from '../utils/axios';
import { localApi } from '../config/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  BuildingOfficeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const UnifiedLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userType: 'company' // default to company, can be 'company', 'sales', 'reservation', 'other_services', or 'accounts'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  const handleUserTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      userType: type
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;

      if (formData.userType === 'company') {
        // For company/admin login
        response = await axios.post(localApi.companyLogin, {
          email: formData.email,
          password: formData.password
        });
      } else {
        // For team member login (sales/reservation/accounts)
        response = await axios.post(localApi.employeeLogin, {
          email: formData.email,
          password: formData.password,
          teamType: formData.userType // Pass teamType for validation
        });
      }

      console.log('Login response:', response.data);

      if (response.data.success) {
        // Clear any existing data first
        localStorage.clear();

        // Store appropriate token and info
        if (formData.userType === 'company') {
          localStorage.setItem('companyToken', response.data.token);
          localStorage.setItem('companyInfo', JSON.stringify(response.data.company));

          // Set user type for proper routing
          const companyData = { ...response.data.company, userType: 'company' };
          localStorage.setItem('companyInfo', JSON.stringify(companyData));

          console.log('Company login successful, redirecting to dashboard');
          window.location.href = '/dashboard';
        } else {
          // Team member login (sales/reservation/accounts)
          localStorage.setItem('employeeToken', response.data.token);

          // STRICT ACCESS CONTROL: Always use the backend teamType (from database)
          // Never let frontend selection override the assigned team
          const backendTeamType = response.data.employee.teamType; // From database - ALWAYS use this

          const employeeData = {
            ...response.data.employee,
            userType: 'employee',
            teamType: backendTeamType // Backend teamType ONLY - no frontend override
          };
          // Save to BOTH keys for compatibility across all components
          localStorage.setItem('employeeInfo', JSON.stringify(employeeData));
          localStorage.setItem('employeeData', JSON.stringify(employeeData));

          console.log(`✅ Employee team from database: ${backendTeamType}`);
          console.log(`${employeeData.teamType} team login successful, redirecting to dashboard`);
          console.log('Employee data:', employeeData);

          // Redirect to unified dashboard
          window.location.href = '/unified-dashboard';
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(
        error.response?.data?.message ||
        'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Gajanan Tours & Travels
          </h1>
          <p className="text-gray-600">
            Enter your email and password to access the system
          </p>
        </div>

        {/* User Type Selection */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-2">Select Your Team:</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 rounded-lg bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => handleUserTypeChange('company')}
              className={`py-2 px-2 rounded-md text-xs font-medium transition-colors ${formData.userType === 'company'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <BuildingOfficeIcon className="h-4 w-4 inline mr-1" />
              Admin
            </button>
            <button
              type="button"
              onClick={() => handleUserTypeChange('sales')}
              className={`py-2 px-2 rounded-md text-xs font-medium transition-colors ${formData.userType === 'sales'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              💼 Sales
            </button>
            <button
              type="button"
              onClick={() => handleUserTypeChange('reservation')}
              className={`py-2 px-2 rounded-md text-xs font-medium transition-colors ${formData.userType === 'reservation'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              📋 Reservation
            </button>
            <button
              type="button"
              onClick={() => handleUserTypeChange('other_services')}
              className={`py-2 px-2 rounded-md text-xs font-medium transition-colors ${formData.userType === 'other_services'
                ? 'bg-teal-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              🎫 Services
            </button>
            <button
              type="button"
              onClick={() => handleUserTypeChange('accounts')}
              className={`py-2 px-2 rounded-md text-xs font-medium transition-colors ${formData.userType === 'accounts'
                ? 'bg-orange-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              💰 Accounts
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            ℹ️ Select your team type before logging in
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email / Username */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {formData.userType === 'branch' ? 'Username / Branch Code' : 'Email Address'}
            </label>
            <div className="relative">
              <input
                type={formData.userType === 'branch' ? 'text' : 'email'}
                name="email"
                id="email"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pl-12"
                placeholder={formData.userType === 'branch' ? 'Enter username or branch code' : 'Enter your email address'}
                value={formData.email}
                onChange={handleChange}
              />
              {formData.userType === 'branch' ? (
                <UserIcon className="h-5 w-5 text-gray-400 absolute left-4 top-4" />
              ) : (
                <EnvelopeIcon className="h-5 w-5 text-gray-400 absolute left-4 top-4" />
              )}
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pl-12 pr-12"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
              />
              <LockClosedIcon className="h-5 w-5 text-gray-400 absolute left-4 top-4" />
              <button
                type="button"
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors ${loading
              ? 'bg-gray-400 cursor-not-allowed'
              : formData.userType === 'company'
                ? 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                : formData.userType === 'branch'
                  ? 'bg-purple-600 hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
                  : 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
              }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                Logging in...
              </div>
            ) : (
              `Login as ${formData.userType === 'company' ? 'Company' : formData.userType === 'branch' ? 'Branch' : 'Employee'}`
            )}
          </button>
        </form>


        {/* <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Default FABTECH Credentials:</h3>
          <div className="text-xs text-blue-700 space-y-1">
            <p><strong>Company Email:</strong> admin@fabtech.com</p>
            <p><strong>Password:</strong> fabtech123</p>
            <p className="text-blue-600 mt-2">⚠️ Please change password after first login</p>
          </div>
        </div> */}

        {/* Company Registration Link */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/company-registration')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Register New Company →
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>© 2024 FABTECH Industries. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLogin;
